import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Cpu, MessageSquareText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useHub } from "@/hub/HubContext";
import { errorMessage } from "@/lib/api";
import { generateRcPython } from "@/rc/python";
import { RcWizard } from "@/components/rc/RcWizard";
import { ProjectTopBar } from "./ProjectTopBar";
import { SetupSummary } from "./SetupSummary";
import { EmptySetup } from "./EmptySetup";
import { HubPanel } from "./HubPanel";
import { ConsolePanel } from "./ConsolePanel";

export const ProjectView = ({ project, name, setName, commitName, saveState, saveSetup, api, guest = false }) => {
  const hub = useHub();
  const cfg = project.config;
  const [tab, setTab] = useState("hub");
  const [busy, setBusy] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [wizard, setWizard] = useState(!cfg);
  const python = useMemo(() => (cfg ? generateRcPython(cfg) : ""), [cfg]);

  const finishWizard = async (next, newName) => {
    try {
      await saveSetup(next, cfg ? undefined : newName);
      setWizard(false);
      toast.success(cfg ? "Setup saved. Press Drive to send it to the hub." : "Your robot is set up. Connect your hub and press Drive.");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const connect = async () => {
    try {
      await hub.connect();
    } catch (e) {
      toast.error(e.message || "Could not connect to the hub");
      hub.addLog("error", e.message || "Could not connect to the hub");
    }
  };

  const drive = async () => {
    setBusy(true);
    setTab("messages");
    try {
      await hub.runPython(python, api);
      toast.success(`${hub.name} is ready. Switch on your controller and drive.`);
    } catch (e) {
      hub.addLog("error", e.message);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const identify = async () => {
    setIdentifying(true);
    try {
      await hub.identify(api);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIdentifying(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col lg:h-screen lg:overflow-hidden" data-testid={guest ? "guest-project-page" : "project-page"}>
      <ProjectTopBar
        name={name}
        onNameChange={setName}
        onNameCommit={commitName}
        saveState={saveState}
        hasSetup={Boolean(cfg)}
        onConnect={connect}
        onDrive={drive}
        onEdit={() => setWizard(true)}
        busy={busy || hub.progress !== null}
        guest={guest}
      />
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <main className="grid-bg min-w-0 flex-1 lg:overflow-y-auto">
          {cfg ? <SetupSummary cfg={cfg} onEdit={() => setWizard(true)} /> : <EmptySetup onStart={() => setWizard(true)} />}
        </main>
        <aside className="flex w-full flex-col border-t bg-background lg:min-h-0 lg:w-[400px] lg:border-l lg:border-t-0 xl:w-[440px]">
          <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
            <TabsList className="mx-4 mt-3 grid grid-cols-2 rounded-full">
              <TabsTrigger value="hub" className="rounded-full" data-testid="hub-tab"><Cpu className="mr-1.5 h-4 w-4" />Hub</TabsTrigger>
              <TabsTrigger value="messages" className="rounded-full" data-testid="messages-tab"><MessageSquareText className="mr-1.5 h-4 w-4" />Hub messages</TabsTrigger>
            </TabsList>
            <TabsContent value="hub" className="mt-0 min-h-0 flex-1 overflow-auto">
              <HubPanel onConnect={connect} onIdentify={identify} identifying={identifying} />
            </TabsContent>
            <TabsContent value="messages" className="mt-2 h-[55vh] min-h-0 flex-1 data-[state=active]:flex data-[state=active]:flex-col lg:h-auto">
              <ConsolePanel />
            </TabsContent>
          </Tabs>
        </aside>
      </div>
      <RcWizard
        open={wizard}
        initial={cfg}
        editing={Boolean(cfg)}
        askName={!cfg}
        onClose={() => setWizard(false)}
        onFinish={finishWizard}
      />
    </div>
  );
};
